import { isTagsEnabled } from '@/helpers/featureFlags'
import { useUserStore } from '@/stores/UserStore'

export default [
  {
    // The redesigned Home. `Homepage` is the name the app reads as "the dealer's home" (the sidebar opens there,
    // the reactivate-account overlay and the referral bubble stay off it), so it moved here with the page.
    path: '/',
    name: 'Homepage',
    // The hub shows its main page here, whatever page it was on.
    meta: { requireAuth: true, requireFields: true, help: false },
    component: () => import('@/views/HomeNew/index.vue'),
    // The new Home belongs to the new design: dealers on the legacy design keep the Home they know.
    // TODO(before-prod): remove when the legacy design is retired.
    beforeEnter: () => (useUserStore().isNewDesign ? true : { name: 'LegacyHome' }),
  },
  {
    // TODO(before-prod): remove, with views/Home/, once the new Home has replaced it for everyone.
    // The previous Home, kept reachable to compare against while the new one is tested.
    path: '/legacy-home',
    name: 'LegacyHome',
    meta: { requireAuth: true, requireFields: true },
    component: () => import('@/views/Home/index.vue'),
  },
  {
    // TODO(before-prod): remove. The new Home's address while it was built; old links and bookmarks land on `/`.
    path: '/home-new',
    redirect: '/',
  },

  {
    path: '/changelog',
    meta: { requireAuth: true, requireFields: true },
    component: () => import('@/views/Home/Changelog/index.vue')
  },
  {
    // Every CDP Hub article; Home's "CDP Hub ›" title opens it.
    path: '/cdp-hub',
    name: 'CdpHub',
    meta: { requireAuth: true, requireFields: true },
    component: () => import('@/views/CdpHub/index.vue'),
  },
  {
    // One CDP Hub article, on the design's article rules.
    path: '/cdp-hub/:feedId',
    name: 'CdpHubArticle',
    meta: { requireAuth: true, requireFields: true },
    component: () => import('@/views/CdpHub/Article/index.vue'),
  },
  {
    path: '/settings',
    meta: { requireAuth: true, requireFields: true },
    component: () => import('@/views/Settings/index.vue'),
    redirect: '/settings/account',
    children: [
      { path: 'account', component: () => import('@/views/Settings/Account.vue') },
      { path: 'plans', name: 'SettingsPlans', component: () => import('@/views/Settings/Plans.vue') },
      { path: 'inventory', component: () => import('@/views/Settings/Inventory.vue') },
      { path: 'notifications', component: () => import('@/views/Settings/Notifications.vue') },
      { path: 'custom-fields', component: () => import('@/views/Settings/CustomFields.vue') },
      { path: 'templates', component: () => import('@/views/Settings/Templates.vue'), meta: { help: 'title-description-templates' } },
      {
        path: 'tags',
        component: () => import('@/views/Settings/Tags.vue'),
        // Behind the `tags` flag, like the nav row that links here.
        beforeEnter: () => (isTagsEnabled() ? true : '/settings/account'),
      },
    ]
  },

  {
    path: '/upgrade-plans',
    name: 'UpgradePlans',
    meta: { requireAuth: true, requireFields: true },
    component: () => import('@/components/UpgradePlans/UpgradeWindow.vue')
  },

  {
    path: '/notifications',
    meta: { requireAuth: true, requireFields: true },
    component: () => import('@/views/Notifications/index.vue')
  },
  {
    path: '/sale-pick-list',
    // Always renders the upgraded Sale Pick List (the `sales_picklist_upgrade`
    // flag was removed); the Dispatcher keeps the legacy view importable.
    // `help` is the hub's page here; the print view has no hub.
    meta: { requireAuth: true, requireFields: true, help: 'pick-orders' },
    component: () => import('@/views/SalePickList/Dispatcher.vue')
  },
  {
    path: '/sale-pick-list/print',
    // `fullScreen` makes App.vue render this route without MainLayout.
    meta: { requireAuth: true, requireFields: true, fullScreen: true },
    component: () => import('@/views/SalePickList/PrintView/index.vue')
  },
  {
    path: '/refer-friend',
    meta: { requireAuth: true, requireFields: true },
    component: () => import('./RouterView.vue')
  },
  {
    path: '/feedback',
    meta: { requireAuth: true, requireFields: true },
    component: () => import('./RouterView.vue')
  },
  {
    path: '/help',
    meta: { requireAuth: true, requireFields: true },
    component: () => import('./RouterView.vue')
  },
  {
    path: '/billing',
    meta: { requireAuth: true, requireFields: true },
    component: () => import('./RouterView.vue')
  },
  {
    path: '/desktop-app',
    meta: { requireAuth: true, requireFields: true },
    component: () => import('./RouterView.vue')
  },
  {
    path: '/pricing',
    meta: { requireAuth: true, requireFields: true },
    component: () => import('./RouterView.vue')
  },
  {
    path: '/exception-detected',
    meta: { requireAuth: true, requireFields: true },
    component: () => import('@/views/ExceptionDetectedPage.vue'),
  },
  {
    // In-app design-system gallery. Lighter-weight alternative to Storybook —
    // each atom / molecule / organism rendered with its key variants.
    // Adding a new component? Drop a `<DSSection>` block into the matching
    // atomic level in `views/DesignSystem/index.vue` + append the name to
    // the `NAV` array so it shows up in the sticky sidebar.
    path: '/design-system',
    name: 'DesignSystem',
    meta: { requireAuth: true, requireFields: true },
    component: () => import('@/views/DesignSystem/index.vue'),
  },
]
